import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type LocaleCode = 'en' | 'nl' | 'fr-FR' | 'fr-CA' | 'es' | 'pt' | 'de' | 'it';

export interface Messages { [key: string]: string | Messages }

interface I18nContextValue {
	locale: LocaleCode;
	messages: Messages;
	t: (key: string, params?: Record<string, unknown>) => string;
	setLocale: (locale: LocaleCode) => void;
	getDirection: () => 'ltr' | 'rtl';
	formatTime: (timeString: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function isRtlLocale(locale: string): boolean {
	return ['ar', 'he', 'fa', 'ur'].some((rtl) => locale.startsWith(rtl));
}

const SUPPORTED: LocaleCode[] = ['en', 'nl', 'fr-FR', 'fr-CA', 'es', 'pt', 'de', 'it'];

function normalizeLocale(input?: string | null): LocaleCode {
	const val = (input || '').trim();
	if (!val) return 'en';
	const lower = val.toLowerCase();
	// direct matches
	if (lower === 'en') return 'en';
	if (lower === 'nl' || lower.startsWith('nl-')) return 'nl';
	if (lower === 'es' || lower.startsWith('es-')) return 'es';
	if (lower === 'pt' || lower.startsWith('pt-')) return 'pt';
	if (lower === 'de' || lower.startsWith('de-')) return 'de';
	if (lower === 'it' || lower.startsWith('it-')) return 'it';
	if (lower === 'fr-ca') return 'fr-CA';
	if (lower === 'fr' || lower === 'fr-fr' || lower.startsWith('fr-')) return 'fr-FR';
	return 'en';
}

function getContainerAttribute(attr: string): string | null {
	try {
		const container = document.getElementById('root');
		return container?.getAttribute(attr) ?? null;
	} catch {
		return null;
	}
}

function detectInitialLocale(): LocaleCode {
	// 1) Container/script attribute
	const containerLang = getContainerAttribute('data-language');
	if (containerLang) return normalizeLocale(containerLang);
	
	// 2) URL param
	try {
		const params = new URLSearchParams(window.location.search);
		const urlLang = params.get('lang');
		if (urlLang) return normalizeLocale(urlLang);
	} catch {}
	
	// 3) Local storage
	try {
		const stored = localStorage.getItem('tenseat_microsite_locale');
		if (stored) return normalizeLocale(stored);
	} catch {}
	
	// 4) Browser
	if (typeof navigator !== 'undefined') {
		const browserLang = navigator.language || (navigator as any).userLanguage;
		return normalizeLocale(browserLang);
	}
	
	return 'en';
}

async function loadMessages(locale: LocaleCode): Promise<Messages> {
	switch (locale) {
		case 'nl':
			return (await import('./locales/nl.ts')).default;
		case 'fr-FR':
			return (await import('./locales/fr-FR.ts')).default;
		case 'fr-CA':
			return (await import('./locales/fr-CA.ts')).default;
		case 'es':
			return (await import('./locales/es.ts')).default;
		case 'pt':
			return (await import('./locales/pt.ts')).default;
		case 'de':
			return (await import('./locales/de.ts')).default;
		case 'it':
			return (await import('./locales/it.ts')).default;
		case 'en':
		default:
			return (await import('./locales/en.ts')).default;
	}
}

function getNested(messages: Messages, key: string): string | undefined {
	const parts = key.split('.');
	let current: any = messages;
	for (const p of parts) {
		if (current && typeof current === 'object' && p in current) {
			current = current[p];
		} else {
			return undefined;
		}
	}
	return typeof current === 'string' ? current : undefined;
}

function applyPlural(template: string, params?: Record<string, unknown>): string {
	if (!params) return template;
	const pluralRegex = /\{(\w+),\s*plural,\s*one\s*\{([^}]*)\}\s*other\s*\{([^}]*)\}\s*\}/g;
	return template.replace(pluralRegex, (_, key: string, one: string, other: string) => {
		const raw = params[key];
		const n = typeof raw === 'number' ? raw : Number(raw);
		const text = n === 1 ? one : other;
		return text.replace(/#/g, String(isNaN(n) ? raw ?? '' : n));
	});
}

function formatTemplate(template: string, params?: Record<string, unknown>): string {
	let out = template;
	out = applyPlural(out, params);
	if (!params) return out;
	return out.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? ''));
}

function formatTimeForLocale(timeString: string, locale: LocaleCode): string {
	try {
		// Parse time string (e.g., "11:30" or "17:00")
		const [hours, minutes] = timeString.split(':').map(Number);
		if (isNaN(hours) || isNaN(minutes)) return timeString;
		
		const date = new Date();
		date.setHours(hours, minutes, 0, 0);
		
		// Use Intl.DateTimeFormat for locale-aware time formatting (same as widget)
		// This automatically handles 12-hour vs 24-hour format based on locale
		const timeFormat = new Intl.DateTimeFormat(locale.replace('-', '_'), {
			hour: 'numeric',
			minute: '2-digit'
		});
		
		return timeFormat.format(date);
	} catch {
		// Fallback to 12-hour format (same as widget)
		const [hours, minutes] = timeString.split(':').map(Number);
		if (isNaN(hours) || isNaN(minutes)) return timeString;
		
		const period = hours >= 12 ? 'PM' : 'AM';
		const displayHours = (hours % 12) || 12;
		return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
	}
}

export const I18nProvider: React.FC<{ children: React.ReactNode; initialLocale?: LocaleCode }> = ({ children, initialLocale }) => {
	const [locale, setLocaleState] = useState<LocaleCode>(normalizeLocale(initialLocale || detectInitialLocale()));
	const [messages, setMessages] = useState<Messages>({});
	const [fallbackMessages, setFallbackMessages] = useState<Messages>({});

	// Load fallback English once
	useEffect(() => {
		loadMessages('en').then(setFallbackMessages).catch(() => setFallbackMessages({}));
	}, []);

	// Load active locale resources
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const msgs = await loadMessages(locale);
				if (!cancelled) {
					setMessages(msgs);
					try { localStorage.setItem('tenseat_microsite_locale', locale); } catch {}
				}
			} catch {
				// On failure, fall back to English
				if (!cancelled) {
					setLocaleState('en');
				}
			}
		})();
		return () => { cancelled = true; };
	}, [locale]);

	const setLocale = useCallback((next: LocaleCode) => {
		if (!SUPPORTED.includes(next)) {
			setLocaleState('en');
		} else {
			setLocaleState(next);
		}
	}, []);

	const t = useCallback((key: string, params?: Record<string, unknown>) => {
		const val = getNested(messages, key) ?? getNested(fallbackMessages, key) ?? key;
		return formatTemplate(val, params);
	}, [messages, fallbackMessages]);

	const getDirection = useCallback(() => (isRtlLocale(locale) ? 'rtl' : 'ltr'), [locale]);

	const formatTime = useCallback((timeString: string) => formatTimeForLocale(timeString, locale), [locale]);

	const value = useMemo<I18nContextValue>(() => ({
		locale,
		messages,
		t,
		setLocale,
		getDirection,
		formatTime,
	}), [locale, messages, t, setLocale, getDirection, formatTime]);

	return (
		<I18nContext.Provider value={value}>
			{children}
		</I18nContext.Provider>
	);
};

export function useI18n(): I18nContextValue {
	const ctx = useContext(I18nContext);
	if (!ctx) throw new Error('useI18n must be used within I18nProvider');
	return ctx;
}

export function getInitialLocale(): LocaleCode {
	return detectInitialLocale();
}
